/**
 * L'anteprima di ripresa della scheda: cosa dice il riquadro 16:9 a destra.
 *
 * ## Perché sta nel dominio
 *
 * Perché è `prevVM` del prototipo v3, e sono tre decisioni: **quale etichetta** porta il distintivo,
 * **da dove** si ripartirebbe, e **quali due tempi** si scrivono sotto. Nessuna delle tre riguarda il
 * disegno, e tutte e tre si sbagliano in silenzio — un'anteprima che dice «RIPRENDI» e riparte da
 * zero non lascia nessuna traccia: nessun errore, nessun log, solo un film ricominciato.
 *
 * ## Le tre etichette
 *
 * - **RIPRENDI** — c'è un punto in cui si era arrivati (fra 1 e 99);
 * - **RIVEDI** — è già stato visto fino in fondo;
 * - **RIPRODUCI** — non è mai stato aperto.
 *
 * Sono tre e non due perché «visto» e «mai iniziato» portano allo stesso posto — l'inizio — ma **non
 * sono la stessa cosa per chi guarda**: sulla seconda si sta scegliendo, sulla prima si sta
 * riguardando. Dirlo cambia la decisione, e costa una parola.
 */

import { WATCHED_PERCENT } from './catalog.js';

export type ResumeBadge = 'resume' | 'rewatch' | 'play';

export interface ResumePreview {
  readonly badge: ResumeBadge;
  /** La barra: la percentuale solo quando c'è davvero qualcosa da riprendere, altrimenti zero. */
  readonly percent: number;
  /** I secondi del punto di ripresa e della durata: chi disegna li formatta. */
  readonly positionSec: number;
  readonly durationSec: number;
  /** Vero quando la riga sotto deve dire «… · riprende da» e i tempi vanno scritti in due. */
  readonly resuming: boolean;
}

/**
 * Compone l'anteprima da quanto si è visto e da quanto dura.
 *
 * `durationMin` può essere `null` — su un catalogo importato la durata manca a macchia — e allora i
 * tempi non si scrivono affatto: **non si inventa un'ora e mezza**. Un tempo plausibile e falso è
 * peggio di nessun tempo, perché nessuno lo mette in dubbio.
 */
export function resumePreview(percent: number, durationMin: number | null): ResumePreview {
  const safePercent = Number.isFinite(percent) && percent > 0 ? Math.min(100, percent) : 0;
  const resuming = safePercent > 0 && safePercent < WATCHED_PERCENT;
  const durationSec = durationMin !== null && durationMin > 0 ? Math.round(durationMin * 60) : 0;
  return {
    badge: resuming ? 'resume' : safePercent >= WATCHED_PERCENT ? 'rewatch' : 'play',
    percent: resuming ? safePercent : 0,
    positionSec: resuming ? Math.round((durationSec * safePercent) / 100) : 0,
    durationSec,
    resuming,
  };
}

/**
 * I secondi in `h:mm:ss` o `m:ss`, come `tf` del prototipo.
 *
 * Fuori dall'i18n di proposito: non è testo, è un orologio — le cifre e i due punti sono gli stessi
 * in tutte e sette le lingue, e passarlo dalle traduzioni darebbe sette occasioni di scriverlo
 * diverso.
 */
export function clockLabel(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '0:00';
  const whole = Math.floor(totalSeconds);
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const seconds = String(whole % 60).padStart(2, '0');
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`;
  return `${minutes}:${seconds}`;
}
